import { Component, OnInit, ViewChild } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Table } from 'primeng/table';
import { ReportService } from '@app/_components/report/report.service';

/**
 * Sales Pipeline Analytics Dashboard.
 *
 * <p>End-to-end view of the full sales pipeline across three channels:
 * Quotes (sent → booked), Holds (direct hold requests), and Upsell Extension
 * Offers (post-booking night extensions). Includes KPIs, daily trends,
 * status mix, and breakdowns by property, reservationist, source, discount
 * tier, and bedroom count.
 *
 * <p>Auto-fires on mount with "yesterday" as the default range (because
 * {@code <app-search-template>} is configured with
 * {@code [displayRecordsOnPageLoad]=true} and the search-template patches the
 * filter dates to yesterday when no explicit defaults are passed).
 *
 * <p>Backend: {@code POST /api/2.6/reports/quote/analytics} →
 * {@code QuoteAnalyticsReportBL.run(...)}.
 *
 * @since 2.6 (quote analytics feature, 2026-05-07)
 */
@Component({
  selector: 'app-quote-analytics',
  templateUrl: './quote-analytics.component.html',
  styleUrls: ['./quote-analytics.component.scss'],
  providers: [MessageService],
  standalone: false
})
export class QuoteAnalyticsComponent implements OnInit {

  // ── Drill-down table references (used by drillDown() to pre-filter) ────────
  @ViewChild('ptDetail')  ptDetail!: Table;
  @ViewChild('ptHDet')    ptHDet!: Table;
  @ViewChild('ptUpsell')  ptUpsell!: Table;
  @ViewChild('ptResDet')  ptResDet!: Table;

  formLoading = false;
  /** Raw report response from the backend; null until first search completes. */
  data: any = null;

  /** Label shown in the active-filter badge above the Quote drill-down. */
  activeQuoteFilter: string | null = null;
  /** Label shown in the active-filter badge above the Hold drill-down. */
  activeHoldFilter:  string | null = null;
  /** Label shown in the active-filter badge above the Upsell drill-down. */
  activeUpsellFilter: string | null = null;
  /** Label shown in the active-filter badge above the Reservation Revenue drill-down. */
  activeResFilter: string | null = null;

  /** quoteStatusLabel value to match, or null = show all. */
  private quoteDrillFilter: string | null = null;
  /** reservationStatusLabel value to match, or null = show all. */
  private holdDrillFilter:  string | null = null;
  /** offerStatus value to match ('APPLIED'|'PENDING'|'EXPIRED'|'DECLINED'), or null = show all. */
  private upsellDrillFilter: string | null = null;
  /** Source key for res revenue drill-down: null=all, 'website'|'hold'|'ota'|'quote'|'og'|'manual' */
  private resDrillFilter: string | null = null;
  /** Group-by mode for the res drill-down table. */
  resGroupBy: 'none' | 'day' | 'month' | 'year' = 'none';

  /** OTA referralSourceId values — must match OTA_SOURCE_IDS in the backend. */
  private readonly OTA_SRC_IDS = new Set([1,3,7,12,13,14,15,16,17,19,20,21]);

  /** Data passed to the Quote drill-down p-table — pre-filtered by KPI click.
   *
   *  Filter modes:
   *   null           → no filter, show all rows
   *   '__SENT__'     → rows where createDate is inside the active search window
   *                    (matches what the "Guests Quoted / Lines Sent" KPI counts)
   *   anything else  → match by quoteStatusLabel (e.g. 'Booked', 'Expired', 'Sent')
   */
  get filteredDetails(): any[] {
    if (!this.data?.details) return [];
    if (!this.quoteDrillFilter) return this.data.details;

    if (this.quoteDrillFilter === '__SENT__') {
      const { start, end } = this.activeWindowBounds();
      return this.data.details.filter((r: any) => {
        if (!r.createDate) return false;
        // createDate arrives as a LocalDateTime ISO string (no timezone suffix).
        // Append T00:00 sentinel is not needed here — we parse as-is but
        // compare to local-midnight bounds so timezone stays consistent.
        const d = new Date(r.createDate).getTime();
        return d >= start && d <= end;
      });
    }

    return this.data.details.filter((r: any) => r.quoteStatusLabel === this.quoteDrillFilter);
  }

  /**
   * Returns start-of-day / end-of-day timestamps for the active search window.
   * In Activity Mode the window is the Sale Date range; in Cohort Mode it is
   * the Quote Create Date range.
   *
   * Parses date values from currentSearchForm which may be either Date objects
   * (from the datepicker) or ISO date strings like "2026-05-20".
   */
  private activeWindowBounds(): { start: number; end: number } {
    const isActivity = this.data?.activityMode;
    const rawStart = isActivity
      ? this.currentSearchForm?.saleDateStart
      : this.currentSearchForm?.filterDateStart;
    const rawEnd = isActivity
      ? this.currentSearchForm?.saleDateEnd
      : this.currentSearchForm?.filterDateEnd;

    const toLocalMidnight = (v: any, endOfDay = false): number => {
      if (!v) return endOfDay ? Infinity : 0;
      // If already a Date object use it directly; if string append time so the
      // engine treats it as local time rather than UTC midnight.
      const d = v instanceof Date ? new Date(v) : new Date(typeof v === 'string' && !v.includes('T') ? v + 'T00:00:00' : v);
      d.setHours(endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0);
      return d.getTime();
    };

    return {
      start: toLocalMidnight(rawStart, false),
      end:   toLocalMidnight(rawEnd,   true)
    };
  }

  /** Data passed to the Hold drill-down p-table — pre-filtered by KPI click.
   *
   *  Filter modes:
   *   null                → no filter, show all rows
   *   '__HOLD_CREATED__'  → rows where holdDate is inside the active search window
   *                         (matches what the "Holds Created" KPI counts)
   *   anything else       → match by reservationStatusLabel (e.g. 'Confirmed', 'Hold')
   */
  get filteredHoldDetails(): any[] {
    if (!this.data?.holdDetails) return [];
    if (!this.holdDrillFilter) return this.data.holdDetails;

    if (this.holdDrillFilter === '__HOLD_CREATED__') {
      const { start, end } = this.activeWindowBounds();
      return this.data.holdDetails.filter((r: any) => {
        if (!r.holdDate) return false;
        const d = new Date(r.holdDate).getTime();
        return d >= start && d <= end;
      });
    }

    return this.data.holdDetails.filter((r: any) => r.reservationStatusLabel === this.holdDrillFilter);
  }

  /** Data passed to the Upsell drill-down p-table — pre-filtered by KPI click.
   *
   *  Filter modes:
   *   null                  → no filter, show all rows
   *   '__UPSELL_CREATED__'  → rows where createdDate is inside the active search window
   *                           (matches what the "Offers Total" KPI counts)
   *   'PENDING'             → OFFERED + ACCEPTED + PARTIALLY_APPLIED (still actionable)
   *   anything else         → match by offerStatus
   */
  get filteredUpsellRows(): any[] {
    if (!this.data?.upsellRows) return [];
    if (!this.upsellDrillFilter) return this.data.upsellRows;

    if (this.upsellDrillFilter === '__UPSELL_CREATED__') {
      const { start, end } = this.activeWindowBounds();
      return this.data.upsellRows.filter((r: any) => {
        if (!r.createdDate) return false;
        const d = new Date(r.createdDate).getTime();
        return d >= start && d <= end;
      });
    }

    if (this.upsellDrillFilter === 'PENDING') {
      return this.data.upsellRows.filter((r: any) =>
        r.offerStatus === 'OFFERED' || r.offerStatus === 'ACCEPTED' || r.offerStatus === 'PARTIALLY_APPLIED'
      );
    }
    return this.data.upsellRows.filter((r: any) => r.offerStatus === this.upsellDrillFilter);
  }

  /** Yesterday's date — used as the default value for the Sale Date pickers so
   *  the page auto-fires with yesterday's booked activity on first load. */
  readonly defaultSaleDate: Date = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d;
  })();

  /** Echo of the last form value so we can re-run on refresh / debug. */
  currentSearchForm: any = null;

  // ── Chart configs ─────────────────────────────────────────────────────────

  /** Daily volume bar chart (sent / booked / expired). */
  dailyChartData: any = {};
  dailyChartOptions: any = {};

  /** Status mix doughnut (QS / QC / QE / QX). */
  statusChartData: any = {};
  statusChartOptions: any = {};

  /** Source pie (which tool created the quote). */
  sourceChartData: any = {};
  sourceChartOptions: any = {};

  /** Discount-tier conversion bar chart. */
  discountChartData: any = {};
  discountChartOptions: any = {};

  constructor(
    private reportService: ReportService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    // No-op: app-search-template fires onSearchSubmit() via displayRecordsOnPageLoad.
  }

  // ─── Search handler ────────────────────────────────────────────────────────

  /** Triggered both by the user clicking Search and by the search-template's
   *  auto-fire on mount. Mirrors the date-shifting pattern used by
   *  ResSalesDashboardComponent so JVM-local times line up. */
  onSearchSubmit(searchForm: any): void {
    this.currentSearchForm = searchForm;
    this.formLoading = true;
    this.data = null;
    this.activeQuoteFilter  = null;
    this.activeHoldFilter   = null;
    this.activeUpsellFilter = null;
    this.quoteDrillFilter   = null;
    this.holdDrillFilter    = null;
    this.upsellDrillFilter  = null;

    const payload: any = { ...searchForm, reportName: 'quote-analytics' };
    // Quote-create window — scopes quote-side metrics
    payload.filterDateStart = this.toIsoDate(searchForm.filterDateStart);
    payload.filterDateEnd   = this.toIsoDate(searchForm.filterDateEnd);
    // Sale-date window — scopes booking-side metrics. When the guest actually
    // closed (str_reservation.saleDate), regardless of when the quote was sent.
    payload.saleDateStart   = this.toIsoDate(searchForm.saleDateStart);
    payload.saleDateEnd     = this.toIsoDate(searchForm.saleDateEnd);

    this.reportService.quoteAnalyticsReport(payload).subscribe({
      next: (res: any) => {
        // Support both ApiResponse-wrapped { data: { quoteAnalytics: {...} } }
        // and a direct envelope shape.
        const envelope = res?.data?.quoteAnalytics ?? res?.data ?? res;
        // Decorate detail rows with a human-readable status label so the
        // drill-down table can show "Sent" instead of "QS" — AND so the
        // column filter matches on the friendly text the user typed.
        if (envelope?.details) {
          envelope.details.forEach((row: any) => {
            row.quoteStatusLabel = this.statusLabel(row.quoteStatus);
          });
        }
        this.data = envelope;
        this.buildCharts(envelope);
        this.formLoading = false;
      },
      error: (err: any) => {
        console.error('quoteAnalyticsReport failed', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Report failed',
          detail: err?.error?.message || err?.message || 'Unable to load quote analytics.',
          life: 8000
        });
        this.formLoading = false;
      }
    });
  }

  // ─── Chart construction ────────────────────────────────────────────────────

  private buildCharts(d: any): void {
    if (!d) return;

    // Daily trend ───────────────────────────────────────────────────────────
    const daily = d.daily ?? [];
    const labels = daily.map((r: any) => this.formatShortDate(r.date));
    this.dailyChartData = {
      labels,
      datasets: [
        { label: 'Lines Sent',    data: daily.map((r: any) => r.linesSent ?? 0),    backgroundColor: '#2563eb', borderRadius: 4 },
        { label: 'Booked',        data: daily.map((r: any) => r.linesBooked ?? 0),  backgroundColor: '#059669', borderRadius: 4 },
        { label: 'Expired',       data: daily.map((r: any) => r.linesExpired ?? 0), backgroundColor: '#dc2626', borderRadius: 4 }
      ]
    };
    this.dailyChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } },
      scales: { x: { stacked: false }, y: { beginAtZero: true, ticks: { precision: 0 } } }
    };

    // Status mix ────────────────────────────────────────────────────────────
    const mix = d.statusMix ?? {};
    this.statusChartData = {
      labels: ['Sent (QS)', 'Booked (QC)', 'Expired (QE)', 'Cancelled (QX)'],
      datasets: [{
        data: [mix.sent ?? 0, mix.booked ?? 0, mix.expired ?? 0, mix.cancelled ?? 0],
        backgroundColor: ['#2563eb', '#059669', '#d97706', '#6b7280']
      }]
    };
    this.statusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } }
    };

    // Source pie ────────────────────────────────────────────────────────────
    const sources = d.bySource ?? [];
    this.sourceChartData = {
      labels: sources.map((s: any) => s.sourceLabel ?? 'Other'),
      datasets: [{
        data: sources.map((s: any) => s.linesSent ?? 0),
        backgroundColor: ['#1e3a8a', '#0891b2', '#7c3aed', '#db2777', '#ea580c', '#65a30d']
      }]
    };
    this.sourceChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } }
    };

    // Discount-tier conversion ──────────────────────────────────────────────
    const tiers = d.byDiscountTier ?? [];
    this.discountChartData = {
      labels: tiers.map((t: any) => t.tier),
      datasets: [
        { label: 'Sent',   data: tiers.map((t: any) => t.linesSent ?? 0),   backgroundColor: '#94a3b8', borderRadius: 4 },
        { label: 'Booked', data: tiers.map((t: any) => t.linesBooked ?? 0), backgroundColor: '#059669', borderRadius: 4 }
      ]
    };
    this.discountChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: 'bottom' } },
      scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
    };
  }

  // ─── Formatting helpers ────────────────────────────────────────────────────

  formatNumber(v: any): string {
    const n = Number(v);
    return isNaN(n) ? '—' : n.toLocaleString();
  }

  formatPct(v: any, alreadyPct = false): string {
    const n = Number(v);
    if (isNaN(n)) return '—';
    return (alreadyPct ? n : n * 100).toFixed(1) + '%';
  }

  formatMoney(v: any): string {
    const n = Number(v);
    if (isNaN(n)) return '—';
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  }

  formatHours(v: any): string {
    if (v == null) return '—';
    const n = Number(v);
    if (isNaN(n)) return '—';
    if (n >= 24) return (n / 24).toFixed(1) + 'd';
    return n.toFixed(1) + 'h';
  }

  formatDays(v: any): string {
    if (v == null) return '—';
    const n = Number(v);
    if (isNaN(n)) return '—';
    return n.toFixed(1) + 'd';
  }

  /** Signed pct change vs prior period KPI of same name. Returns ▲/▼ + value. */
  delta(metric: string, lowerIsBetter = false): { text: string; positive: boolean } {
    const cur = this.data?.kpis?.[metric];
    const prior = this.data?.priorKpis?.[metric];
    const c = Number(cur);
    const p = Number(prior);
    if (isNaN(c) || isNaN(p) || p === 0) {
      return { text: '—', positive: false };
    }
    const pct = ((c - p) / p) * 100;
    const arrow = pct >= 0 ? '▲' : '▼';
    const positive = lowerIsBetter ? c < p : c > p;
    return { text: `${arrow} ${Math.abs(pct).toFixed(1)}%`, positive };
  }

  /**
   * Map the 2-char quote status code to a human-readable label.
   * QS/QC/QE/QX/QR are reservationist shorthand; the dashboard is also seen
   * by non-engineers (managers, owners) who shouldn't have to memorize codes.
   * See QuoteDetailVO constants on the backend for the source of truth.
   */
  statusLabel(code: string | null | undefined): string {
    switch (code) {
      case 'QS': return 'Sent';
      case 'QC': return 'Booked';
      case 'QE': return 'Expired';
      case 'QX': return 'Cancelled';
      case 'QR': return 'Requested';
      default:   return code ?? '—';
    }
  }

  // ─── KPI drill-down ────────────────────────────────────────────────────────

  /**
   * Click handler for KPI tiles.
   * Clears the target table, optionally pre-filters it, then smooth-scrolls
   * to the drill-down section.
   *
   * @param tableType  'quote' → Quote drill-down; 'hold' → Hold drill-down
   * @param filterValue  The value to match (e.g. 'Booked', 'Expired').
   *                     Pass '' to clear all filters and show every row.
   * @param filterLabel  Human-readable label shown in the active-filter badge.
   */
  drillDown(tableType: 'quote' | 'hold' | 'upsell', filterValue: string, filterLabel: string): void {
    const anchor = tableType === 'quote'  ? 'quote-detail-anchor'
                 : tableType === 'hold'   ? 'hold-detail-anchor'
                 :                          'upsell-detail-anchor';

    if (tableType === 'quote') {
      this.quoteDrillFilter = filterValue || null;
      this.activeQuoteFilter = filterLabel || null;
      // Reset to page 1 so the filtered rows are immediately visible.
      if (this.ptDetail) { this.ptDetail.first = 0; }
    } else if (tableType === 'hold') {
      this.holdDrillFilter = filterValue || null;
      this.activeHoldFilter = filterLabel || null;
      if (this.ptHDet) { this.ptHDet.first = 0; }
    } else {
      this.upsellDrillFilter  = filterValue || null;
      this.activeUpsellFilter = filterLabel || null;
      if (this.ptUpsell) { this.ptUpsell.first = 0; }
    }

    // Defer scroll one tick so Angular finishes rendering the filtered rows.
    setTimeout(() => {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  // ─── Reservation Revenue drill-down ────────────────────────────────────────

  /** Flat rows filtered by the active source filter.
   *  Each row is augmented with `statusLabel` (human-readable) so the p-table
   *  column filter can match "Confirmed" / "Owner Guest" rather than raw codes.
   */
  get filteredResDetails(): any[] {
    const rows: any[] = this.data?.resDetails ?? [];
    const filtered = this.resDrillFilter
      ? rows.filter((r: any) => this.matchesResFilter(r, this.resDrillFilter!))
      : rows;
    // Augment with readable statusLabel — avoids calling resStatusLabel() from
    // within the p-table filter engine which only sees raw field values.
    return filtered.map((r: any) => {
      const nights = Number(r.numberOfNightsStay) || 0;
      const rev    = Number(r.revenue)             || 0;
      return {
        ...r,
        statusLabel: this.resStatusLabel(r.reservationStatus),
        avgNight:    nights > 0 ? rev / nights : 0
      };
    });
  }

  private matchesResFilter(r: any, f: string): boolean {
    const ctx   = r.referralSourceContext as number;
    const src   = r.referralSourceId      as number;
    const isOta = this.OTA_SRC_IDS.has(src);
    // OTA (order-level channel) takes priority over referralSourceContext so
    // the drill-down filter is consistent with the backend KPI buckets.
    switch (f) {
      case 'website': return r.reservationStatus === 'OP' && ctx === 1 && !isOta;
      case 'hold':    return r.reservationStatus === 'OP' && ctx === 2 && !isOta;
      case 'ota':     return r.reservationStatus === 'OP' && isOta;
      case 'quote':   return r.reservationStatus === 'OP' && ctx === 3 && !isOta;
      case 'og':      return r.reservationStatus === 'OG';
      case 'manual':  return r.reservationStatus === 'OP'
                          && ![1,2,3].includes(ctx)
                          && !isOta;
      default:        return true;
    }
  }

  /**
   * When resGroupBy !== 'none', aggregates filteredResDetails into period buckets.
   * Each bucket: { period, sortKey, count, totalNights, totalRevenue }.
   * When 'none', returns the flat filtered rows (same as filteredResDetails).
   */
  get groupedResDetails(): any[] {
    const rows = this.filteredResDetails;
    if (this.resGroupBy === 'none') return rows;

    const fmtPeriod = (d: string | null): string => {
      if (!d) return '—';
      const dt = new Date(d + 'T00:00:00');
      if (this.resGroupBy === 'day')
        return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      if (this.resGroupBy === 'month')
        return dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      return String(dt.getFullYear());
    };
    const keyOf = (d: string | null): string => {
      if (!d) return '—';
      const dt = new Date(d + 'T00:00:00');
      if (this.resGroupBy === 'day')   return dt.toISOString().slice(0, 10);
      if (this.resGroupBy === 'month') return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}`;
      return String(dt.getFullYear());
    };

    const map = new Map<string, any>();
    for (const r of rows) {
      const k = keyOf(r.saleDate);
      if (!map.has(k)) {
        map.set(k, { period: fmtPeriod(r.saleDate), sortKey: k,
                     count: 0, totalNights: 0, totalRevenue: 0 });
      }
      const g = map.get(k)!;
      g.count++;
      g.totalNights  += r.numberOfNightsStay ?? 0;
      g.totalRevenue += Number(r.revenue ?? 0);
    }
    return Array.from(map.values()).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }

  /**
   * Click handler for Reservation Revenue KPI tiles.
   * @param filter  null = show all; 'website'|'hold'|'ota'|'quote'|'og'|'manual'
   * @param label   Human-readable label for the active-filter badge.
   */
  drillDownRes(filter: string | null, label: string | null): void {
    this.resDrillFilter = filter;
    this.activeResFilter = label;
    this.resGroupBy = 'none';
    if (this.ptResDet) this.ptResDet.first = 0;
    setTimeout(() => {
      document.getElementById('res-detail-anchor')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  clearResFilter(): void {
    this.resDrillFilter  = null;
    this.activeResFilter = null;
    if (this.ptResDet) this.ptResDet.first = 0;
  }

  /**
   * Merged team-performance rows — joins `byTeam` (quote/hold pipeline) with
   * `resReservationistRows` (reservation revenue by booking source) on
   * reservationistName.
   *
   * Result includes every reservationist who appears in either array.
   * Sorted by totalRevenue descending so top earners appear first.
   */
  get mergedTeamRows(): any[] {
    const resRows: any[]  = this.data?.resReservationistRows ?? [];
    const teamRows: any[] = this.data?.byTeam ?? [];

    if (!resRows.length && !teamRows.length) return [];

    // Build a mutable lookup so we can track which byTeam rows get consumed.
    const teamMap = new Map<string, any>();
    teamRows.forEach(r => teamMap.set(r.reservationistName ?? '', r));

    // Base: revenue rows enriched with pipeline metrics
    const merged: any[] = resRows.map(r => {
      const name = r.reservationistName ?? '';
      const team = teamMap.get(name) ?? {};
      teamMap.delete(name);
      return {
        ...r,
        quotesSent:          team.quotesSent          ?? 0,
        guestsBooked:        team.guestsBooked        ?? 0,
        activeQuotes:        team.activeQuotes        ?? 0,
        quoteConversionRate: team.quoteConversionRate ?? 0,
        holdsCreated:        team.holdsCreated        ?? 0,
        holdsConfirmed:      team.holdsConfirmed      ?? 0,
        holdConversionRate:  team.holdConversionRate  ?? 0,
      };
    });

    // Remainder: pipeline-only rows (sent quotes/holds but no confirmed reservations)
    teamMap.forEach(team => {
      merged.push({
        reservationistName:  team.reservationistName,
        websiteCount:  0, websiteRevenue:  0,
        holdCount:     0, holdRevenue:     0,
        otaCount:      0, otaRevenue:      0,
        quoteCount:    0, quoteRevenue:    0,
        ogCount:       0, ogRevenue:       0,
        manualCount:   0, manualRevenue:   0,
        totalCount:    0, totalRevenue:    0,
        quotesSent:          team.quotesSent          ?? 0,
        guestsBooked:        team.guestsBooked        ?? 0,
        activeQuotes:        team.activeQuotes        ?? 0,
        quoteConversionRate: team.quoteConversionRate ?? 0,
        holdsCreated:        team.holdsCreated        ?? 0,
        holdsConfirmed:      team.holdsConfirmed      ?? 0,
        holdConversionRate:  team.holdConversionRate  ?? 0,
      });
    });

    // Sort by total revenue descending — matches default sort on the table
    return merged.sort((a, b) => Number(b.totalRevenue ?? 0) - Number(a.totalRevenue ?? 0));
  }

  /** Resets the active KPI filter on the given drill-down table. */
  clearDrillDown(tableType: 'quote' | 'hold' | 'upsell'): void {
    if (tableType === 'quote') {
      this.quoteDrillFilter  = null;
      this.activeQuoteFilter = null;
      if (this.ptDetail) { this.ptDetail.first = 0; }
    } else if (tableType === 'hold') {
      this.holdDrillFilter  = null;
      this.activeHoldFilter = null;
      if (this.ptHDet) { this.ptHDet.first = 0; }
    } else {
      this.upsellDrillFilter  = null;
      this.activeUpsellFilter = null;
      if (this.ptUpsell) { this.ptUpsell.first = 0; }
    }
  }

  /**
   * Generic column-sum helper for footer totals.
   * Safe with null/undefined rows or missing fields.
   */
  sumBy(rows: any[] | null | undefined, field: string): number {
    if (!rows?.length) return 0;
    return rows.reduce((acc: number, r: any) => acc + (Number(r[field]) || 0), 0);
  }

  /**
   * Quote drill-down footer: sum of quotePrice × numNights across given rows.
   */
  sumQuoteTotal(rows: any[] | null | undefined): number {
    if (!rows?.length) return 0;
    return rows.reduce((acc: number, r: any) => acc + ((Number(r['quotePrice']) || 0) * (Number(r['numNights']) || 0)), 0);
  }

  /** Human-readable label for reservation status codes shown in the Res Detail table. */
  resStatusLabel(status: string | null): string {
    if (!status) return '—';
    switch (status) {
      case 'OP': return 'Confirmed';
      case 'OG': return 'Owner Guest';
      case 'OX': return 'Cancelled';
      case 'OC': return 'Cancelled';
      case 'QH': return 'Hold';
      case 'QE': return 'Expired';
      case 'PP': return 'Postponed';
      default:   return status;
    }
  }

  private toIsoDate(d: any): string | null {
    if (!d) return null;
    try {
      const dt = (d instanceof Date) ? d : new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    } catch {
      return null;
    }
  }

  private formatShortDate(d: any): string {
    if (!d) return '';
    try {
      const dt = (d instanceof Date) ? d : new Date(d);
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return String(d);
    }
  }
}
